import * as H from 'history';

import type { RootState } from './store';
import { getBookingStripe as resumeHotelStripeExpress } from '@store/slices/review-book';
import { getBookingStripe as resumeCondoStripeExpress } from '@store/slices/condo-review-book';
import { getCarsBookingStripe } from '@store/slices/cars-review-book';
import { getWeeksBookingStripe } from '@store/slices/weeks/weeks-review-book';
import { ISessionKey } from '@share/common-types';

import {
  getStripeCheckoutBackHref,
  hydrateStripeExpressReturnSearch,
  type StripeExpressVertical,
} from './stripe-return-url';

/** Marker put on `history.replace` state when a resume failure is sent back to the checkout page,
 * so the checkout wrapper can surface the error inline (see {@link redirectToCheckoutFromStripeResume}). */
export type StripeResumeRedirectState = { stripeResumeError?: string };

/**
 * Reads (and consumes) a stripe-resume error from `history.location.state`.
 * After consumption, replaces the current location with `state: null` so a refresh doesn't re-trigger
 * the error display. Returns the message or `null` if no resume error was queued for this page.
 */
export function consumeStripeResumeErrorFromLocation(history: H.History<H.LocationState>): string | null {
  const state = history.location.state as StripeResumeRedirectState | undefined | null;
  const msg = state && typeof state.stripeResumeError === 'string' && state.stripeResumeError.length > 0
    ? state.stripeResumeError
    : null;
  if (msg) {
    history.replace({
      pathname: history.location.pathname,
      search: history.location.search,
      hash: history.location.hash,
      state: null,
    });
  }
  return msg;
}

/**
 * Send the user from a `/stripe/{vertical}/…` page back to the regular checkout page (which has the
 * full booking context), carrying the resume failure message in `history.state` so the checkout
 * wrapper can dispatch it onto the store and render it inline like a normal checkout error.
 */
export function redirectToCheckoutFromStripeResume(
  history: H.History<H.LocationState>,
  errorMessage: string,
): void {
  const { pathname, search } = history.location;
  const href = getStripeCheckoutBackHref(pathname, search);
  const [nextPath, nextQs = ''] = href.split('?');
  history.replace({
    pathname: nextPath,
    search: nextQs ? `?${nextQs}` : '',
    state: { stripeResumeError: errorMessage } as StripeResumeRedirectState,
  });
}

function buildSessionKey(sessionId: string): ISessionKey {
  return { value: sessionId, isInvalid: false, expireDate: '' };
}

const STRIPE_RESUME_QUERY_KEYS = [
  'bookingId',
  'booking_id',
  'sessionId',
  'session_id',
  'paymentMethod',
  'payment_method',
  'redirect_status',
  'ser',
] as const;

/** Ensures `sessionId` is present for hotel/condo/weeks resume when only stored in session/local state. */
export function mergeStripeExpressResumeSearch(
  locationSearch: string,
  fallbackSessionKey?: ISessionKey | null,
): string {
  const params = new URLSearchParams(locationSearch);
  const existing =
    params.get('sessionId') ??
    params.get('session_id');
  if (fallbackSessionKey?.value && !existing) {
    params.set('sessionId', fallbackSessionKey.value);
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

/**
 * Outcome of {@link runStripeExpressResumeFromQuery}:
 * - `ran: false` → no `bookingId` in the URL (caller should show "no booking" / search).
 * - `ran: true, error: null` → resume succeeded; query params already stripped.
 * - `ran: true, error: <msg>, isResumeError: false` → failure *before* we hit book-resume
 *   (e.g. missing session, hydration failure). Caller should show the full-page Stripe error.
 * - `ran: true, error: <msg>, isResumeError: true` → failure returned by (or thrown during)
 *   the book-resume call itself. Caller should redirect the user back to the regular checkout
 *   page and surface the error there (the stripe URL doesn't carry enough info to retry).
 */
export type StripeExpressResumeResult = {
  ran: boolean;
  error: string | null;
  isResumeError?: boolean;
};

export async function runStripeExpressResumeFromQuery(options: {
  dispatch: (action: any) => any;
  getState: () => RootState;
  history: H.History<H.LocationState>;
  pathname: string;
  search: string;
  vertical: StripeExpressVertical;
}): Promise<StripeExpressResumeResult> {
  const { dispatch, getState, history, vertical } = options;
  hydrateStripeExpressReturnSearch(history);

  const pathname = history.location.pathname;
  const params = new URLSearchParams(history.location.search || '');
  const bookingId =
    params.get('bookingId') ??
    params.get('booking_id') ??
    params.get('BookingId');
  const sessionId =
    params.get('sessionId') ??
    params.get('session_id') ??
    '';
  const paymentMethod =
    params.get('paymentMethod') ??
    params.get('payment_method') ??
    'Card';
  const redirectStatus = params.get('redirect_status');
  const isCancel = redirectStatus === 'failed' || redirectStatus === 'canceled';

  if (!bookingId) {
    return { ran: false, error: null };
  }

  if (vertical !== 'cars' && !sessionId) {
    return { ran: true, error: 'Missing session.', isResumeError: false };
  }

  const sessionKey = buildSessionKey(sessionId);

  try {
    if (vertical === 'hotel') {
      await dispatch(resumeHotelStripeExpress(bookingId, sessionKey, paymentMethod, isCancel) as never);
    } else if (vertical === 'condo') {
      await dispatch(resumeCondoStripeExpress(bookingId, sessionKey, paymentMethod, isCancel) as never);
    } else if (vertical === 'weeks') {
      await dispatch(getWeeksBookingStripe(bookingId, sessionKey, paymentMethod, isCancel) as never);
    } else {
      await dispatch(getCarsBookingStripe(bookingId, sessionKey, paymentMethod, isCancel) as never);
    }

    const s = getState();
    let err: string | null = null;
    if (vertical === 'hotel') {
      const r = s.reviewBookStore;
      err = r.stripeError || r.errorBooking || null;
    } else if (vertical === 'condo') {
      const r = s.condoReviewBookStore;
      err = r.errorStripe || r.errorBooking || null;
    } else if (vertical === 'weeks') {
      err = s.weeksReviewBookStore.errorBooking || null;
    } else {
      const r = s.carsReviewBookStore;
      err = r.errorStripe || r.errorBooking || null;
    }

    if (!err) {
      for (const k of STRIPE_RESUME_QUERY_KEYS) {
        params.delete(k);
      }
      params.delete('BookingId');
      const next = params.toString();
      history.replace({ pathname, search: next ? `?${next}` : '' });
    }

    return { ran: true, error: err, isResumeError: !!err };
  } catch {
    return { ran: true, error: 'Payment confirmation failed.', isResumeError: true };
  }
}
